import { Grid } from '@mui/material';
import * as CSS from 'csstype';
import { useFocusScroll } from '../../../../../hooks/useFocusScroll';
import { NavigableElement } from '../../../CfDataGridProps';
import { getColumnCount } from '../../../utility/data-grid-props';
import { StyledCard } from '../CfDataGridCardView.styles';
import { CfDataGridSkeletonCard } from './CfDataGridSkeletonCard';
import { CfDataGridCardCommonProps } from './common';

export interface CfDataGridCardProps<TItem> extends CfDataGridCardCommonProps<TItem> {
  item: TItem | undefined;
  after?: boolean;
}

export function CfDataGridCard<TItem>(props: CfDataGridCardProps<TItem>): JSX.Element {
  const item = props.item;
  if (!item) {
    return <CfDataGridSkeletonCard cardViewDefinition={props.cardViewDefinition} orientation={props.orientation} />;
  }

  const virtualizedRow = props.cardViewDefinition.virtualizedProps?.enabled ?? false;
  const [elementRef, handleFocus] = useFocusScroll<HTMLTableRowElement>(0, virtualizedRow);

  const columnCount = getColumnCount(props.cardViewDefinition, props.orientation);

  const content = props.cardViewDefinition.getContent(
    item,
    props.index,
    (navigableElement: NavigableElement) => {
      const rowIndex = Math.floor(props.index / columnCount);
      const columnIndex = props.index % columnCount;
      props.setNavigableElement(navigableElement, rowIndex, columnIndex);
    },
    props.recomputeRowHeights,
    props.recomputeRowHeight,
    props.after
  );

  let cursorStyle: CSS.Property.Cursor = 'default';
  if (props.onItemClicked) {
    cursorStyle = 'pointer';
  }

  if (props.cursorStyle !== undefined) {
    if (typeof props.cursorStyle === 'function') {
      cursorStyle = props.cursorStyle(item, 'table');
    } else {
      cursorStyle = props.cursorStyle;
    }
  }

  let hover = true;

  if (props.hoverStyle !== undefined) {
    if (typeof props.hoverStyle === 'function') {
      hover = props.hoverStyle(item, 'table');
    } else {
      hover = props.hoverStyle;
    }
  }
  return (
    <Grid item xs={12 / columnCount} ref={elementRef} onFocus={handleFocus} sx={props.gridItemSx}>
      <StyledCard
        onClick={(event) => {
          // eslint-disable-next-line
          const target = event.target as unknown as any;
          if (target?.nodeName === 'BUTTON' || target?.offsetParent?.nodeName === 'BUTTON') return;
          props.onItemClicked?.(item, 'card', props.index);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          // eslint-disable-next-line
          const target = event.target as unknown as any;
          if (target?.nodeName === 'BUTTON' || target?.offsetParent?.nodeName === 'BUTTON') return;
          if (event.key === 'Enter') props.onItemClicked?.(item, 'card', props.index);
        }}
        sx={{
          padding: 2,
          cursor: cursorStyle,
          ...(virtualizedRow ? { height: '100%' } : {}),
          ...props.cardSx,
        }}
        data-hover={hover}
      >
        {content}
      </StyledCard>
    </Grid>
  );
}
